import { countries } from 'countries-list'
import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod/v4'
import { billingDetailsSchema } from '@/components/form/billing-details/schema'
import { StateFormField } from '@/components/form/billing-details/state-form-field'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

type Props = {
  isDisabled: boolean
}

export const BillingDetailsForm = ({ isDisabled }: Props) => {
  const isPending = isDisabled
  const form = useFormContext<z.infer<typeof billingDetailsSchema>>()
  const [isCountryListOpen, setCountryList] = useState(false)
  const country = form.watch('country')

  const countryList = useMemo(
    () =>
      Object.entries(countries)
        .sort(([, c1], [, c2]) => c1.name.localeCompare(c2.name))
        .map(([code, country]) => ({
          code: code.toLowerCase().trim(),
          name: country.name,
        })),
    []
  )

  const selectedCountryName = useMemo(() => {
    const found = countryList.find(
      (c) => c.code.toLowerCase() === country?.toLowerCase()
    )
    return found?.name
  }, [country, countryList])

  return (
    <>
      <FormField
        control={form.control}
        name="name"
        disabled={isPending}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Name</FormLabel>
            <FormControl>
              <Input autoComplete="off" aria-label="Name" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
        <FormField
          control={form.control}
          disabled={isPending}
          name="country"
          render={() => (
            <FormItem>
              <FormLabel>Country</FormLabel>
              <Popover open={isCountryListOpen} onOpenChange={setCountryList}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isDisabled}
                      className="w-full justify-between text-left font-normal"
                    >
                      {selectedCountryName || 'Select country'}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search country..." />
                    <CommandList>
                      <CommandEmpty>No country found.</CommandEmpty>
                      <CommandGroup>
                        {countryList.map(({ code, name }) => (
                          <CommandItem
                            key={code}
                            value={name}
                            onSelect={() => {
                              form.setValue('country', code)
                              setCountryList(false)
                            }}
                          >
                            {name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />
        <StateFormField isDisabled={isPending} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
        <FormField
          control={form.control}
          disabled={isPending}
          name="city"
          render={({ field }) => (
            <FormItem>
              <FormLabel>City</FormLabel>
              <FormControl>
                <Input aria-label="City" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          disabled={isPending}
          name="zip"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ZIP</FormLabel>
              <FormControl>
                <Input aria-label="ZIP code" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
        <FormField
          control={form.control}
          disabled={isPending}
          name="street"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address line 1</FormLabel>
              <FormControl>
                <Input aria-label="Address line 1" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          disabled={isPending}
          name="street2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address line 2</FormLabel>
              <FormControl>
                <Input aria-label="Address line 2" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  )
}
